import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Repeat,
  TrendingUp,
  Bot,
  Target,
  Wallet,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Star,
  Activity,
  BarChart3,
  Layers,
  Info,
  Globe,
  Bell,
  Shield,
  Zap,
  Bitcoin,
  Eye,
  EyeOff,
  Gift,
  CreditCard
} from 'lucide-react';
import { TradingMode, UserStatus } from '../App';
import { MarketData } from '../hooks/useDatabase';
import { User as UserType } from '@supabase/supabase-js';
import { useMarketData } from '../contexts/MarketDataContext';
import { useBybitData } from '../contexts/BybitDataContext';
import { TOP_CRYPTO_PAIRS } from '../constants/tradingPairs';



const convertBalance = (balance: number, targetCurrency: string): number => {
  if (targetCurrency === 'USD') return balance; // base case

  // market_data stores prices like EUR/USD, GBP/USD, BTC/USDT etc.


  
  const conversionSymbol = targetCurrency === 'BTC' || targetCurrency === 'ETH' 
    ? `${targetCurrency}USDT`
    : `USD/${targetCurrency}`;

  const rate = getPriceBySymbol(conversionSymbol);

  if (!rate || rate <= 0) return balance; // fallback: no conversion
  // If we stored USD/XXX → multiply, if XXX/USD → divide
  if (conversionSymbol.startsWith('USD/')) {
    return balance * rate; // 1 USD = X target currency
  } else {
    return balance / rate; // target in USD, so invert
  }
};


interface HeaderProps {
  tradingMode: TradingMode;
  setTradingMode: (mode: TradingMode) => void;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  marketData?: MarketData;
  usdtBalance: number;
  btcBalance: number;
  totalPortfolioValue: number;
  user: UserType;
  signOut: () => void;
  marketDataList: MarketData[];
  userStatus: UserStatus;
  isDemoAccount: boolean;
}

const Header: React.FC<HeaderProps> = ({
  tradingMode,
  setTradingMode,
  selectedPair,
  setSelectedPair,
  currentPrice,
  marketData: selectedMarketData,
  usdtBalance,
  btcBalance,
  totalPortfolioValue,
  user,
  signOut,
  marketDataList,
  userStatus,
  isDemoAccount
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { marketData: cfdMarketData, isConnected: cfdConnected, getPriceBySymbol: getCfdPrice } = useMarketData();
  const { getPriceBySymbol: getCryptoPrice, isConnected: cryptoConnected } = useBybitData();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getPriceBySymbol = (symbol: string): number => {
    if (tradingMode === 'futures') {
      return getCryptoPrice(symbol);
    }
    return getCfdPrice(symbol);
  };

  // Smart price lookup that uses the correct data source regardless of trading mode
  const getConversionRate = (symbol: string): number => {
    // For crypto pairs (BTCUSDT, ETHUSDT, etc.), always use Bybit
    if (symbol.includes('USDT') && !symbol.includes('/')) {
      return getCryptoPrice(symbol);
    }
    // For forex pairs (USD/EUR, EUR/USD, etc.), always use CFD
    return getCfdPrice(symbol);
  };

  const marketData = tradingMode === 'futures'
    ? TOP_CRYPTO_PAIRS.map(pair => ({
        symbol: pair.symbol,
        price: getCryptoPrice(pair.symbol),
        name: pair.name,
        type: pair.type
      }))
    : cfdMarketData;
  const [showBalances, setShowBalances] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

// --- Currency Dropdown State ---
const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
const [selectedDisplayCurrency, setSelectedDisplayCurrency] = useState('USD');
const currencyRef = useRef<HTMLDivElement>(null);

// Available currencies
const currencyOptions = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'BTC', symbol: '₿', name: 'Bitcoin' },
  { code: 'ETH', symbol: 'Ξ', name: 'Ethereum' },
];

// Get symbol for currently selected currency
const selectedCurrencySymbol = React.useMemo(() => {
  const option = currencyOptions.find(opt => opt.code === selectedDisplayCurrency);
  return option ? option.symbol : '$';
}, [selectedDisplayCurrency]);

  const convertBalance = (balance: number, targetCurrency: string): number => {
    if (targetCurrency === 'USD') return balance;

    // For crypto, use direct USDT pairs from Bybit
    if (targetCurrency === 'BTC' || targetCurrency === 'ETH') {
      const cryptoSymbol = `${targetCurrency}USDT`;
      const rate = getConversionRate(cryptoSymbol);
      if (rate && rate > 0) {
        return balance / rate; // Convert USD to crypto (e.g., $100 / $60000 = 0.00166 BTC)
      }
      return balance; // Fallback
    }

    // For fiat currencies, try both USD/XXX and XXX/USD formats from CFD
    const directSymbol = `USD/${targetCurrency}`; // e.g., USD/EUR
    const invertedSymbol = `${targetCurrency}/USD`; // e.g., EUR/USD

    // Try direct symbol first
    let rate = getConversionRate(directSymbol);
    if (rate && rate > 0) {
      return balance * rate; // If USD/EUR = 0.93, then $100 = €93
    }

    // Try inverted symbol
    rate = getConversionRate(invertedSymbol);
    if (rate && rate > 0) {
      return balance / rate; // If EUR/USD = 1.08, then $100 = €92.59 (100/1.08)
    }

    // Fallback: no conversion available
    return balance;
  };
  // Refs for dropdown handling
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const pairSelectorRef = useRef<HTMLDivElement>(null);

  // Get current price for selected pair
  const getCurrentPairPrice = () => {
    // Use the MarketDataContext's getPriceBySymbol which handles symbol conversion
    const price = getPriceBySymbol(selectedPair);
    if (price > 0) return price;
    
    // Fallback to currentPrice prop
    return currentPrice || 0;
  };

  const currentPairPrice = getCurrentPairPrice();
  const currentBtcPrice = getPriceBySymbol('BTCUSDT') || 0;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (pairSelectorRef.current && !pairSelectorRef.current.contains(event.target as Node)) {
        setShowPairSelector(false);
        setSearchTerm('');
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setIsCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigation items
  const navigationItems = [
    { key: 'home', label: t('navigation.home'), icon: Home },
    { key: 'swap', label: t('trading.swap'), icon: Repeat },
    { key: 'futures', label: t('trading.futures'), icon: TrendingUp },
    { key: 'cfd', label: t('trading.cfd'), icon: BarChart3 },
    { key: 'prop_firm', label: t('propFirm.prop'), icon: Target },
    { key: 'robot', label: t('trading.robot'), icon: Bot },
    { key: 'events', label: t('trading.events'), icon: Activity },
    { key: 'staking', label: t('trading.staking'), icon: Layers },
    { key: 'wheel', label: 'Spin Wheel', icon: Gift }
  ];

  // Get status color based on user tier
  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'No-Coiner': return 'text-slate-500';
      case 'Shrimp': return 'text-gray-400';
      case 'Crab': return 'text-purple-400';
      case 'Octopus': return 'text-purple-400';
      case 'Dolphin': return 'text-blue-400';
      case 'Shark': return 'text-red-400';
      case 'Whale': return 'text-green-400';
      case 'Humpback': return 'text-cyan-400';
      default: return 'text-slate-400';
    }
  };

  // Get status icon based on user tier
  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case 'No-Coiner': return '🚫';
      case 'Shrimp': return '🦐';
      case 'Crab': return '🦀';
      case 'Octopus': return '🐙';
      case 'Dolphin': return '🐬';
      case 'Shark': return '🦈';
      case 'Whale': return '🐋';
      case 'Humpback': return '🐳';
      default: return '📊';
    }
  };

  // Format pair display
  const formatPairDisplay = (symbol: string) => {
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  // Get appropriate decimal places based on asset type
  const getDecimalPlaces = (symbol: string, price: number) => {
    // Crypto pairs - 2-8 decimals based on price
    if (symbol.endsWith('USDT')) {
      if (price < 0.01) return 8;
      if (price < 1) return 6;
      if (price < 10) return 4;
      return 2;
    }

    // Forex pairs - 5 decimals
    if (symbol.includes('/') && !symbol.includes('XAU') && !symbol.includes('XAG') && !symbol.includes('XPT') && !symbol.includes('XPD')) {
      return 5;
    }

    // Commodities (Gold, Silver, Oil, etc.) - 3 decimals
    if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('XPT') || symbol.includes('XPD') ||
        symbol.includes('NATGAS') || symbol.includes('BCO') || symbol.includes('WTICO') ||
        symbol.includes('CORN') || symbol.includes('WHEAT') || symbol.includes('SOYBN') ||
        symbol.includes('SUGAR') || symbol.includes('COFFEE') || symbol.includes('COTTON') ||
        symbol.includes('COCOA')) {
      return 3;
    }

    // Stocks - 3 decimals
    return 3;
  };

  // Format price with appropriate decimal places
  const formatPrice = (symbol: string, price: number) => {
    const decimals = getDecimalPlaces(symbol, price);
    return price.toFixed(decimals);
  };

  // Filter pairs based on search term
  const getFilteredPairs = () => {
    // For CFD mode, use only the hardcoded CFD instruments
    if (tradingMode === 'cfd') {
      const HARDCODED_CFD_INSTRUMENTS = [
        // Forex Pairs
        { symbol: 'EUR/USD', name: 'Euro/US Dollar', type: 'forex' },
        { symbol: 'GBP/USD', name: 'British Pound/US Dollar', type: 'forex' },
        { symbol: 'AUD/USD', name: 'Australian Dollar/US Dollar', type: 'forex' },
        { symbol: 'NZD/USD', name: 'New Zealand Dollar/US Dollar', type: 'forex' },
        { symbol: 'EUR/JPY', name: 'Euro/Japanese Yen', type: 'forex' },
        { symbol: 'GBP/JPY', name: 'British Pound/Japanese Yen', type: 'forex' },
        { symbol: 'EUR/GBP', name: 'Euro/British Pound', type: 'forex' },
        { symbol: 'EUR/AUD', name: 'Euro/Australian Dollar', type: 'forex' },
        { symbol: 'EUR/CHF', name: 'Euro/Swiss Franc', type: 'forex' },
        { symbol: 'GBP/AUD', name: 'British Pound/Australian Dollar', type: 'forex' },
        { symbol: 'GBP/CHF', name: 'British Pound/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/CAD', name: 'Australian Dollar/Canadian Dollar', type: 'forex' },
        { symbol: 'AUD/CHF', name: 'Australian Dollar/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/JPY', name: 'Australian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CAD/JPY', name: 'Canadian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CHF/JPY', name: 'Swiss Franc/Japanese Yen', type: 'forex' },
        { symbol: 'NZD/JPY', name: 'New Zealand Dollar/Japanese Yen', type: 'forex' },

        // Commodities
        { symbol: 'XAG/USD', name: 'Silver/USD', type: 'commodity' },
        { symbol: 'NATGAS/USD', name: 'Natural Gas', type: 'commodity' },
        { symbol: 'BCO/USD', name: 'Brent Crude', type: 'commodity' },
        { symbol: 'WTICO/USD', name: 'WTI Crude Oil', type: 'commodity' },
        { symbol: 'XPT/USD', name: 'Platinum/USD', type: 'commodity' },
        { symbol: 'XAU/USD', name: 'Gold/USD', type: 'commodity' },
        { symbol: 'XPD/USD', name: 'Palladium/USD', type: 'commodity' },
        { symbol: 'CORN/USD', name: 'Corn', type: 'commodity' },
        { symbol: 'WHEAT/USD', name: 'Wheat', type: 'commodity' },
        { symbol: 'SOYBN/USD', name: 'Soybean', type: 'commodity' },
        { symbol: 'SUGAR/USD', name: 'Sugar', type: 'commodity' },
        { symbol: 'COFFEE/USD', name: 'Coffee', type: 'commodity' },
        { symbol: 'COTTON/USD', name: 'Cotton', type: 'commodity' },
        { symbol: 'COCOA/USD', name: 'Cocoa', type: 'commodity' },

        // Stocks
        { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
        { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
        { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock' },
        { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
        { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
        { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
        { symbol: 'NFLX', name: 'Netflix Inc.', type: 'stock' },
        { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
        { symbol: 'BA', name: 'Boeing Company', type: 'stock' },
        { symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock' },
        { symbol: 'GS', name: 'Goldman Sachs', type: 'stock' },
        { symbol: 'BAC', name: 'Bank of America', type: 'stock' },
        { symbol: 'WMT', name: 'Walmart', type: 'stock' },
        { symbol: 'DIS', name: 'Disney', type: 'stock' },
        { symbol: 'CVX', name: 'Chevron', type: 'stock' }
      ];
      
      if (!searchTerm) {
        return HARDCODED_CFD_INSTRUMENTS.map(item => ({
          ...item,
          price: getCfdPrice(item.symbol)
        }));
      }

      const term = searchTerm.toLowerCase();
      return HARDCODED_CFD_INSTRUMENTS
        .filter(data =>
          data.symbol.toLowerCase().includes(term) ||
          data.name.toLowerCase().includes(term)
        )
        .map(item => ({
          ...item,
          price: getCfdPrice(item.symbol)
        }));
    }
    
    if (!searchTerm) {
      if (tradingMode === 'futures') {
        return TOP_CRYPTO_PAIRS.map(pair => ({
          symbol: pair.symbol,
          price: getCryptoPrice(pair.symbol),
          name: pair.name,
          type: pair.type
        }));
      }
      return cfdMarketData;
    }

    const term = searchTerm.toLowerCase();

    if (tradingMode === 'futures') {
      return TOP_CRYPTO_PAIRS
        .filter(pair =>
          pair.symbol.toLowerCase().includes(term) ||
          pair.name.toLowerCase().includes(term) ||
          formatPairDisplay(pair.symbol).toLowerCase().includes(term)
        )
        .map(pair => ({
          symbol: pair.symbol,
          price: getCryptoPrice(pair.symbol),
          name: pair.name,
          type: pair.type
        }));
    }
    
    // For other modes, apply search to all market data
    return marketData.filter(data => 
      data.symbol.toLowerCase().includes(term) ||
      formatPairDisplay(data.symbol).toLowerCase().includes(term)
    );
  };

  const usesWideDesktopHeader = tradingMode === 'futures';
  const desktopHeaderBreakpoint = usesWideDesktopHeader ? '2xl:flex' : 'xl:flex';
  const compactHeaderBreakpoint = usesWideDesktopHeader ? '2xl:hidden' : 'xl:hidden';
  const headerContainerClass = 'w-full px-3 sm:px-4 lg:px-8';
  const headerShellBackgroundClass = 'bg-slate-900/95 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_34%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.16),transparent_32%)]';
  const headerPanelBackgroundClass = 'bg-gradient-to-br from-blue-500/22 via-indigo-500/16 to-purple-500/18';
  const headerSurfaceBackgroundClass = 'bg-gradient-to-br from-blue-500/10 via-indigo-500/8 to-purple-500/12';
  const headerSurfaceHoverBackgroundClass = 'hover:bg-gradient-to-br hover:from-blue-500/16 hover:via-indigo-500/12 hover:to-purple-500/18';
  const headerSelectedBackgroundClass = 'bg-gradient-to-r from-blue-500/18 to-purple-500/16';
  const headerDenseDropdownButtonClass = 'border border-purple-500/30 bg-[linear-gradient(135deg,rgba(11,17,36,0.985),rgba(24,35,68,0.99),rgba(43,25,78,0.985))] shadow-lg shadow-slate-950/35 hover:border-purple-400/45 hover:bg-[linear-gradient(135deg,rgba(18,26,48,0.99),rgba(33,45,84,0.992),rgba(56,34,95,0.99))]';
  const headerDenseDropdownPanelClass = 'border border-purple-500/30 bg-[linear-gradient(180deg,rgba(10,15,34,0.998)_0%,rgba(19,27,58,0.995)_48%,rgba(37,20,67,0.992)_100%)] shadow-2xl shadow-slate-950/80 ring-1 ring-white/5';
  const headerDenseDropdownItemHoverClass = 'hover:bg-[linear-gradient(135deg,rgba(61,88,152,0.2),rgba(94,54,166,0.22))]';

 return (
  <header className={`sticky top-0 z-40 border-b border-slate-700/50 ${headerShellBackgroundClass} shadow-xl backdrop-blur-md`}>
    <div className={headerContainerClass}>
      <div className="flex min-h-16 items-center justify-between gap-3 py-2 sm:h-16 sm:py-0">

        {/* Left Section - Logo and Navigation */}
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3 lg:gap-5">
          {/* Logo */}
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2.5">
            <img
              src="/ideality.svg"
              alt="Ideality"
              className="h-7 w-auto max-w-[8.75rem] object-contain min-[420px]:h-8 sm:h-9 sm:max-w-[10rem]"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className={`hidden items-center gap-1 ${desktopHeaderBreakpoint}`}>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => setTradingMode(item.key as TradingMode)}
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-all duration-300 ${
                    tradingMode === item.key
                      ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/25'
                      : `text-slate-400 hover:text-white ${headerSurfaceHoverBackgroundClass}`
                  }`}
                >
                  <Icon size={14} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Center Section - Pair Selector and Price */}
        {(tradingMode === 'futures' || tradingMode === 'cfd' || tradingMode === 'prop_firm') && (
          <div className={`hidden items-center gap-2.5 2xl:gap-3 ${desktopHeaderBreakpoint}`}>
            {/* Pair Selector */}
            <div className="relative" ref={pairSelectorRef}>
              <button
                onClick={() => setShowPairSelector(!showPairSelector)}
                className={`flex items-center gap-2 rounded-xl border border-slate-600/50 ${headerSurfaceBackgroundClass} px-2.5 py-1.5 text-sm transition-all hover:border-slate-500/50 ${headerSurfaceHoverBackgroundClass} 2xl:px-3.5`}
              >
                <span className="max-w-[132px] truncate font-semibold text-white 2xl:max-w-none">{formatPairDisplay(selectedPair)}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {showPairSelector && (
                <div className={`absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 shadow-xl`}>
                  {/* Search Input */}
                  <div className="border-b border-slate-700 p-2.5">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        placeholder="Search pairs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full rounded-lg border border-slate-600 bg-slate-800 py-1.5 pl-9 pr-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                    </div>
                  </div>

                  {/* Pairs List */}
                  <div className="max-h-64 overflow-y-auto">
                    {getFilteredPairs().map((data) => (
                      <button
                        key={data.symbol}
                        onClick={() => {
                          setSelectedPair(data.symbol);
                          setShowPairSelector(false);
                          setSearchTerm('');
                        }}
                        className={`flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors ${headerSurfaceHoverBackgroundClass} ${
                          selectedPair === data.symbol ? `${headerSelectedBackgroundClass} text-blue-400` : 'text-white'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <Star size={13} className="text-slate-600" />
                          <span className="font-medium">{formatPairDisplay(data.symbol)}</span>
                        </div>
                        <span className="font-mono text-xs text-slate-400">
                          ${formatPrice(data.symbol, data.price || 0)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Current Price */}
            <div className={`flex w-fit items-center justify-between rounded-xl border border-slate-600/50 ${headerPanelBackgroundClass} px-2.5 py-2.5 text-white 2xl:px-3.5`}>
              <div className="flex items-center gap-2.5">
                <span className="text-xs font-medium">{t('header.price')}</span>
              </div>
              <span className="ml-2.5 text-[13px] font-mono">${formatPrice(selectedPair, currentPairPrice)}</span>
            </div>
          </div>
        )}

        {/* Right Section - Portfolio + User Status + Menu */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-2.5 lg:gap-3">
          {/* Portfolio Value + Currency Dropdown */}
          <div className={`hidden min-w-0 items-center gap-2 2xl:gap-4 ${desktopHeaderBreakpoint}`}>
            <div className={`flex min-w-0 max-w-[220px] items-center justify-between rounded-xl ${headerPanelBackgroundClass} px-3 py-2.5 text-white xl:max-w-none xl:px-3.5`}>
              <div className="flex min-w-0 items-center gap-2 xl:gap-2.5">
                <button
                  onClick={() => setShowBalances(!showBalances)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {showBalances ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                <div className="min-w-0">
                  <div className="text-slate-400 text-xs">{t('header.portfolioValue')}</div>
                  <div className="truncate text-[13px] font-mono text-white">
                    {showBalances
                      ? `${selectedCurrencySymbol}${convertBalance(
                          totalPortfolioValue,
                          selectedDisplayCurrency
                        ).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '••••••'}
                  </div>
                </div>
              </div>

              {/* Currency Dropdown */}
              <div className="relative ml-2 xl:ml-2.5" ref={currencyRef}>
                <button
                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                  className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-white transition-all ${headerDenseDropdownButtonClass}`}
                >
                  {selectedCurrencySymbol} <ChevronDown size={13} />
                </button>
                {isCurrencyDropdownOpen && (
                  <div className={`absolute right-0 z-50 mt-2 max-h-80 w-44 overflow-y-auto rounded-lg [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${headerDenseDropdownPanelClass}`}>
                    {currencyOptions.map((option) => (
                      <button
                        key={option.code}
                        onClick={() => {
                          setSelectedDisplayCurrency(option.code);
                          setIsCurrencyDropdownOpen(false);
                        }}
                        className={`flex w-full items-center px-3 py-2 text-xs transition-colors ${headerDenseDropdownItemHoverClass} ${
                          selectedDisplayCurrency === option.code
                            ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white'
                            : 'text-white'
                        }`}
                      >
                        {option.symbol} {option.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Account Type Indicator */}
            <div className={`hidden rounded-xl border px-2.5 py-1.5 md:block ${
              isDemoAccount
                ? 'bg-purple-500/10 border-purple-500/30'
                : 'bg-gradient-to-r from-purple-500/20 to-violet-500/10 border-purple-500/30'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isDemoAccount ? 'bg-purple-400' : 'bg-white'
                }`} />
                <span className={`text-xs font-semibold ${
                  isDemoAccount ? 'text-purple-400' : 'text-white'
                }`}>
                  {isDemoAccount ? 'DEMO' : 'LIVE'}
                </span>
              </div>
            </div>
          </div>

          {/* User Status Badge */}
          <div
            className={`flex min-w-0 items-center gap-1.5 rounded-xl border px-2 py-1.5 sm:px-2.5 ${
              userStatus === 'Whale' || userStatus === 'Humpback'
                ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30'
                : userStatus === 'Shark'
                ? 'bg-gradient-to-r from-red-500/20 to-pink-500/20 border-red-500/30'
                : userStatus === 'Dolphin'
                ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/30'
                : userStatus === 'Octopus'
                ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-purple-500/30'
                : userStatus === 'Crab'
                ? 'bg-gradient-to-r from-purple-500/20 to-violet-500/20 border-purple-500/30'
                : `${headerSurfaceBackgroundClass} border-slate-600/50`
            }`}
          >
            <span className="text-base">{getStatusIcon(userStatus)}</span>
            <span className={`hidden truncate text-[11px] font-medium min-[420px]:block sm:text-xs ${getStatusColor(userStatus)}`}>
              {t(`header.tiers.${userStatus.toLowerCase()}`)}
            </span>
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center gap-1.5 rounded-lg p-1.5 text-slate-400 transition-colors ${headerSurfaceHoverBackgroundClass} hover:text-white`}
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-violet-500">
                <User size={14} className="text-white" />
              </div>
              <ChevronDown size={14} className="hidden sm:block" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-xl border border-purple-500/35 bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 shadow-2xl shadow-slate-950/80 sm:max-w-[calc(100vw-2rem)]">
                <div className="border-b border-slate-700 p-3">
                  <div className="flex items-center gap-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-white">{user.email}</div>
                      <div className={`text-xs ${getStatusColor(userStatus)}`}>
                        {getStatusIcon(userStatus)} {userStatus}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <button
                    onClick={() => {
                      setTradingMode('wallet');
                      setShowUserMenu(false);
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-slate-300 transition-colors ${headerSurfaceHoverBackgroundClass} hover:text-white`}
                  >
                    <Wallet size={15} />
                    <span>{t('navigation.wallet')}</span>
                  </button>

                  <button
                    onClick={() => {
                      setTradingMode('profile');
                      setShowUserMenu(false);
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-slate-300 transition-colors ${headerSurfaceHoverBackgroundClass} hover:text-white`}
                  >
                    <User size={15} />
                    <span>{t('navigation.profile')}</span>
                  </button>

                  <button
                    onClick={() => {
                      signOut();
                      setShowUserMenu(false);
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-slate-300 transition-colors ${headerSurfaceHoverBackgroundClass} hover:text-white`}
                  >
                    <LogOut size={15} />
                    <span>{t('navigation.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`${compactHeaderBreakpoint} rounded-lg p-1.5 text-slate-400 transition-colors ${headerSurfaceHoverBackgroundClass} hover:text-white`}
          >
            {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
    </div>

    {/* Mobile Menu */}
    {isMobileMenuOpen && (
      <div className={`border-t border-slate-700/50 py-3 ${compactHeaderBreakpoint}`}>
        <div className={headerContainerClass}>
        {/* Mobile Portfolio Value */}
        <div className={`mb-4 rounded-xl border border-slate-600/50 ${headerPanelBackgroundClass} p-3.5`}>
          <div className="mb-3 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-slate-400 text-xs">{t('header.portfolioValue')}</div>
              <div className="truncate text-base font-mono text-white">
                {showBalances
                  ? `$${totalPortfolioValue.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : '••••••'}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <button
                onClick={() => setShowBalances(!showBalances)}
                className="p-1 text-slate-400 transition-colors hover:text-white"
              >
                {showBalances ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
              <div className={`flex min-w-0 items-center gap-1 rounded-lg px-2 py-1 ${getStatusColor(userStatus)}`}>
                <span>{getStatusIcon(userStatus)}</span>
                <span className="truncate text-xs font-medium">{userStatus}</span>
              </div>
            </div>
          </div>

          {/* Account Type Indicator - Mobile */}
          <div className="flex items-center justify-center">
            <div className={`rounded-lg border px-3 py-1.5 ${
              isDemoAccount
                ? 'bg-purple-500/10 border-purple-500/30'
                : 'bg-gradient-to-r from-purple-500/20 to-violet-500/10 border-purple-500/30'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isDemoAccount ? 'bg-purple-400' : 'bg-white'
                }`} />
                <span className={`text-xs font-semibold ${
                  isDemoAccount ? 'text-purple-400' : 'text-white'
                }`}>
                  {isDemoAccount ? 'DEMO ACCOUNT' : 'LIVE ACCOUNT'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                onClick={() => {
                  setTradingMode(item.key as TradingMode);
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-all duration-300 ${
                  tradingMode === item.key
                    ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/25'
                    : `text-slate-400 hover:text-white ${headerSurfaceHoverBackgroundClass} border border-slate-600/30`
                }`}
              >
                <Icon size={15} />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile Pair Selector */}
        {(tradingMode === 'futures' || tradingMode === 'cfd' || tradingMode === 'prop_firm') && (
          <div className="mt-3 border-t border-slate-700/50 pt-3" ref={pairSelectorRef}>
            <div className="mb-2">
              <div className="text-slate-400 text-xs mb-2">Current Pair</div>
              <button
                onClick={() => setShowPairSelector(!showPairSelector)}
                className={`flex w-full items-center justify-between rounded-xl border border-slate-600/50 ${headerSurfaceBackgroundClass} px-3.5 py-2.5 text-sm transition-all ${headerSurfaceHoverBackgroundClass}`}
              >
                <span className="truncate pr-3 font-semibold text-white">{formatPairDisplay(selectedPair)}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>

              {showPairSelector && (
                <div className={`mt-2 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-700 rounded-xl shadow-xl z-50 w-full overflow-hidden`}>
                  {/* Search Input */}
                  <div className="border-b border-slate-700 p-2.5">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        placeholder="Search pairs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full rounded-lg border border-slate-600 bg-slate-800 py-1.5 pl-9 pr-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      />
                    </div>
                  </div>

                  {/* Pairs List */}
                  <div className="max-h-64 overflow-y-auto">
                    {getFilteredPairs().map((data) => (
                      <button
                        key={data.symbol}
                        onClick={() => {
                          setSelectedPair(data.symbol);
                          setShowPairSelector(false);
                          setSearchTerm('');
                          setIsMobileMenuOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${headerSurfaceHoverBackgroundClass} ${
                          selectedPair === data.symbol ? `${headerSelectedBackgroundClass} text-blue-400` : 'text-white'
                        }`}
                      >
                        <span className="truncate font-medium">{formatPairDisplay(data.symbol)}</span>
                        <span className="shrink-0 text-xs font-mono text-slate-400">
                          ${formatPrice(data.symbol, data.price || 0)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    )}
  </header>
   
);
};


  export default Header;
